import { CustomInputField } from "@/components/chat/input/CustomInputField";
import { ExternalLinkArea } from "@/components/common/ExternalLinkArea";
import { useMessages } from "@/hooks/api/messages/useMessages";
import { useSendMessage } from "@/hooks/api/messages/useSendMessage";
import { useServices } from "@/hooks/api/services/useServices";
import { useThreads } from "@/hooks/api/threads/useThreads";
import { Messages, UserMessage } from "@/models/Message";
import { makeStyles, Text, tokens } from "@fluentui/react-components";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ulid } from "ulid";
import { ServiceButton } from "../components/common/button/ServiceButton";

export const ServiceSelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const newThreadId = ulid();
  const { mutate: updateThreads } = useThreads();
  const { data: services } = useServices();
  const [recipientId, setRecipientId] = useState<string | undefined>(undefined);

  const { mutate: updateMessages } = useMessages(newThreadId, { isPaused: () => true });
  const { trigger: sendMessage } = useSendMessage(newThreadId);

  const handleServiceClick = (serviceName: string) => {
    if (services) {
      const service = services.find(service => service.name === serviceName);
      if (service) {
        setRecipientId(service.id);
      }
    }
  };

  const handleSendMessage = async (message: string, recipientServiceId?: string) => {
    if (!recipientServiceId) {
      // この画面では必須
      console.error("Recipient ID is null");
      return;
    }

    const newMessage = new UserMessage({
      id: ulid(),
      threadId: newThreadId,
      sender: "",
      recipient: recipientServiceId,
      content: message,
    });
    updateMessages(new Messages([newMessage]), { revalidate: false });
    navigate(`/threads/${newThreadId}`);
    await sendMessage(newMessage);
    updateThreads();
    updateMessages();
  };

  const classes = useStyles();
  return (
    <div className={classes.container}>
      <div className={classes.mainWrapper}>
        <div className={classes.headerSection}>
          <Text as="h1" className={classes.title}>
            お手伝いできることはありますか？
          </Text>
          <Text as="p" className={classes.subtitle}>
            問い合わせたいチャットを選択してください
          </Text>
        </div>

        <div className={classes.buttonSection}>
          {services?.map((service) => (
            <ServiceButton
              key={service.id}
              text={service.name}
              onClick={() => handleServiceClick(service.name)}
            />
          ))}
        </div>

        <div className={classes.inputSection}>
          <CustomInputField
            services={services}
            onSendMessage={handleSendMessage}
            recipientId={recipientId}
          />
          <Text as="p" className={classes.disclaimerText}>
            回答内容はAIにて自動生成されており、現時点で100%の回答精度を保証するものではなく、参考情報として取り扱いください。
            今後の精度向上のためにも積極的なフィードバックをお願いします。
          </Text>
        </div>

        <div className={classes.serviceSelectionAreaContainer}>
          <ExternalLinkArea />
        </div>
      </div>
    </div>
  );
};

const useStyles = makeStyles({
  container: {
    display: "flex",
    alignItems: "center",
    height: "100%",
    justifyContent: "center",
    background: tokens.colorNeutralBackground1,
  },
  mainWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: "640px",
  },
  headerSection: {
    textAlign: "center",
    marginBottom: tokens.spacingVerticalXL,
  },
  title: {
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    textAlign: "center",
    marginBottom: tokens.spacingVerticalS,
    display: "block",
  },
  subtitle: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    textAlign: "center",
    display: "block",
  },

  buttonSection: {
    display: "flex",
    justifyContent: "flex-start",
    gap: tokens.spacingHorizontalS,
    width: "100%",
    maxWidth: "640px",
    marginBottom: tokens.spacingVerticalM,
  },
  inputSection: {
    width: "100%",
    maxWidth: "640px",
    marginBottom: tokens.spacingVerticalL,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    flexDirection: "column",
    display: "flex",
    alignItems: "center",
  },
  disclaimerText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    textAlign: "center",
    lineHeight: tokens.lineHeightBase300,
    marginBottom: tokens.spacingVerticalXXXL,
  },
  faqSection: {
    width: "100%",
    maxWidth: "800px",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    padding: 0,
    overflow: "hidden",
  },
  serviceSelectionAreaContainer: {
    width: "100%",
    maxWidth: "640px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    padding: 0,
    overflow: "hidden",
  },
});
